// Phase 46 — pure helper module. Pulled out of `lib/api.js` so jest unit
// tests can exercise it without importing axios (which is ESM and not
// transformed by the default CRA jest config). `lib/api.js` re-exports
// this function so existing call sites keep working unchanged.
export function formatApiError(detail) {
  // Phase 46 — hardened. Accepts either a raw `detail` payload (from
  // `err.response.data.detail`) OR the entire axios error/exception
  // object. Guarantees a non-empty STRING return so callers can safely
  // pass the result straight to `toast.error()` / JSX without risking
  // the "Objects are not valid as a React child" crash that surfaced
  // in Phase 45's quick-create flow.
  if (detail == null) return "Something went wrong. Please try again.";

  // Axios-style error: pluck the detail and recurse.
  if (typeof detail === "object" && detail !== null && "response" in detail) {
    const apiDetail = detail?.response?.data?.detail;
    if (apiDetail != null) return formatApiError(apiDetail);
    // Network / CORS / aborted: fall back to the Error.message string.
    if (typeof detail.message === "string" && detail.message) return detail.message;
    return "Network error. Please try again.";
  }

  if (typeof detail === "string") return detail.trim() || "Something went wrong. Please try again.";

  if (Array.isArray(detail)) {
    // Pydantic 422 → list of { type, loc, msg, input, url } objects.
    // Render each entry as "field: msg" so it's both human-readable
    // and points at the offending field.
    const parts = detail
      .map((e) => {
        if (e == null) return "";
        if (typeof e === "string") return e;
        if (typeof e === "object") {
          if (typeof e.msg === "string") {
            const loc = Array.isArray(e.loc)
              ? e.loc.filter((p) => p !== "body" && typeof p === "string").join(".")
              : "";
            return loc ? `${loc}: ${e.msg}` : e.msg;
          }
          try { return JSON.stringify(e); } catch { return "[error]"; }
        }
        return String(e);
      })
      .filter(Boolean);
    return parts.join(" · ") || "Something went wrong. Please try again.";
  }

  if (typeof detail === "object") {
    if (typeof detail.msg === "string") return detail.msg;
    if (typeof detail.detail === "string") return detail.detail;
    if (typeof detail.message === "string") return detail.message;
    try { return JSON.stringify(detail); } catch { return "[error]"; }
  }

  return String(detail);
}

// Phase 47 — split a 422 error into per-field messages so the form can
// outline the offending input(s) red and scroll the first one into view.
// Returns:
//   {
//     fieldErrors: { "<field_name>": "msg", ... },
//     generalMessage: "joined human-readable summary",
//   }
// `fieldErrors` is always a plain string-keyed map. Non-field errors
// (network, opaque) only populate `generalMessage`.
export function extractFieldErrors(err) {
  const out = { fieldErrors: {}, generalMessage: "" };

  // Walk to the raw `detail` payload — accept either an axios error or
  // an already-extracted detail.
  let detail = err;
  if (detail && typeof detail === "object" && "response" in detail) {
    detail = detail?.response?.data?.detail;
  }

  if (Array.isArray(detail)) {
    const general = [];
    for (const e of detail) {
      if (!e || typeof e !== "object") {
        if (typeof e === "string") general.push(e);
        continue;
      }
      const msg = typeof e.msg === "string" ? e.msg : "";
      // Pydantic `loc` is normally ["body", "<field>", ...nested].
      // Pick the first non-"body" string as the form field name.
      let field = "";
      if (Array.isArray(e.loc)) {
        for (const part of e.loc) {
          if (typeof part === "string" && part !== "body") {
            field = part;
            break;
          }
        }
      }
      if (field && msg) {
        // Don't overwrite if multiple errors point at the same field —
        // keep the FIRST so the user sees the most relevant message
        // first (Pydantic usually returns the topmost violation first).
        if (!out.fieldErrors[field]) out.fieldErrors[field] = msg;
        general.push(`${field}: ${msg}`);
      } else if (msg) {
        general.push(msg);
      }
    }
    out.generalMessage = general.join(" · ") || formatApiError(err);
    return out;
  }

  // Non-array detail (string, opaque object, network) → no field map.
  out.generalMessage = formatApiError(err);
  return out;
}
